import React, {useEffect, useMemo, useState} from 'react';
import {Loader2, PlusIcon, TrashIcon} from 'lucide-react';
import {createColumnHelper, flexRender, getCoreRowModel, useReactTable} from '@tanstack/react-table';
import {AppraiserCoverageArea, SelectOption} from "../../../api/ApiTypes.ts";
import {SamSelect} from "../../../components/SamSelect.tsx";
import {NotificationType, useNotification} from "../../../context/NotificationContext.tsx";
import {useAppraiserApi, useConstantsApi} from "../../../context/ApiContext.tsx";
import {AppraiserProfileTabProps} from "./AppraiserProfile.tsx";
import Tooltip from "../../../components/Tooltip.tsx";

export const CountyCoverageArea: React.FC<AppraiserProfileTabProps> = ({appraiserInfo}) => {
    const [coverageAreas, setCoverageAreas] = useState<AppraiserCoverageArea[]>([]);
    const [score, setScore] = useState<number>(0);
    const [states, setStates] = useState<SelectOption[]>([]);
    const [counties, setCounties] = useState<SelectOption[]>([]);
    const [selectedState, setSelectedState] = useState<string>(states[0]?.value || '');
    const [selectedCounty, setSelectedCounty] = useState<string>(counties[0]?.value || '');
    const { showNotification } = useNotification();
    const constantsApi = useConstantsApi();
    const appraiserApi = useAppraiserApi();
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!selectedState) return;
        constantsApi.getCountiesForState(selectedState)
            .then(setCounties)
            .catch(console.log)
    }, [selectedState]);

    useEffect(() => {
        constantsApi.getStates()
            .then(setStates)
            .catch(console.log)

        fetchCoverageAreas();
    }, [appraiserInfo?.id]);

    const fetchCoverageAreas = async () => {
        try {
            if (!appraiserInfo?.id) {
                setCoverageAreas([]);
                return;
            }
            setIsLoading(true);
            const response = await appraiserApi.getCoverageAreas(appraiserInfo.id);
            if (response?.appraiserCoverageAreas) {
                setCoverageAreas(response.appraiserCoverageAreas);
            } else {
                setCoverageAreas([]);
            }
            if (response?.averageScore) {
                setScore(response.averageScore);
            }
            setIsLoading(false);
        } catch (error) {
            console.log('Error fetching coverage areas:', error);
            setCoverageAreas([]);
        }
    };

    const handleAddCoverageArea = async () => {

        const hasStateAndCounty = selectedState && selectedCounty;
        const hasAppraiserInfo = !!appraiserInfo;

        if (!hasAppraiserInfo || !hasStateAndCounty) {
            showNotification(
                NotificationType.ERROR,
                "Please select a state and county"
            );
            return;
        }
        setIsLoading(true);
        await appraiserApi.addCoverageArea({
            appraiserId: appraiserInfo.id,
            county: selectedCounty,
            state: selectedState
        }).then(response => {
                if (response?.appraiserCoverageAreas) {
                    setCoverageAreas(response?.appraiserCoverageAreas);
                    showNotification(NotificationType.SUCCESS, 'Coverage area added successfully');
                }
                else {
                    showNotification(NotificationType.ERROR, 'Failed to add coverage area');
                }
            })
            .catch(console.log)
            .finally(()=> {
                setIsLoading(false);
            })
    };

    const handleRemoveCoverageArea = async (toDelete: AppraiserCoverageArea) => {
        if (!toDelete || !toDelete.id) {
            showNotification(NotificationType.ERROR, 'Invalid coverage area');
            return;
        }

        appraiserApi.deleteCoverageArea(
            {
                appraiserId: toDelete.appraiserProfile,
                coverageAreaId: toDelete.id,
                county: toDelete.coverageCounty,
                state: toDelete.coverageStateCode}
        )
            .then(response => {
                if (response?.appraiserCoverageAreas) {
                    setCoverageAreas(response?.appraiserCoverageAreas);
                    showNotification(NotificationType.SUCCESS, 'Coverage area removed successfully');
                }
                else {
                    showNotification(NotificationType.ERROR, 'Failed to remove coverage area');
                }
            })
            .catch(console.log)
    };

    const columnHelper = createColumnHelper<AppraiserCoverageArea>();

    const columns = useMemo(
        () => [
            columnHelper.accessor('coverageStateCode', {
                header: 'State',
                cell: info => info.getValue()
            }),
            columnHelper.accessor('coverageCounty', {
                header: 'County',
                cell: info => info.getValue()
            }),
            columnHelper.accessor('zipCode', {
                header: 'Zip Code',
                cell: info => info.getValue()
            }),
            columnHelper.display({
                header: 'Actions',
                cell: info => (
                    <div className="flex items-center justify-center">
                        <Tooltip text='Delete'>
                            <button
                                onClick={() => handleRemoveCoverageArea(info.row.original)}
                                className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 hover:cursor-pointer"
                            >
                                <TrashIcon className="h-4 w-4" />
                            </button>
                        </Tooltip>
                    </div>
                )
            })
        ],
        []
    );

    const table = useReactTable({
        data: coverageAreas || [],
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <div className="space-y-6">
            <div className="bg-white rounded-lg border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Add Coverage Area</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                    <SamSelect
                        value={selectedState}
                        onChange={setSelectedState}
                        options={states}
                        className="mt-0"
                    />
                    <SamSelect
                        value={selectedCounty}
                        onChange={setSelectedCounty}
                        options={counties}
                        disabled={!selectedState}
                        className="mt-0"
                    />
                    <button
                        onClick={handleAddCoverageArea}
                        disabled={!(selectedState && selectedCounty) || isLoading}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 hover:cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isLoading ? (
                            <>
                                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                                Adding...
                            </>
                        ) : (
                            <>
                                <PlusIcon className="h-5 w-5 mr-2" />
                                Add Coverage Area
                            </>
                        )}
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-lg border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Current Coverage Areas</h3>
                <p>Current Appraiser Score: {score === 0 ? 'Not enough data' : score}</p>
                <div className="mt-4">
                    {isLoading ? (
                        <div className="p-4 flex justify-center items-center">
                            <Loader2 className="h-8 w-8 text-blue-500 animate-spin mb-2" />
                        </div>
                    ) : (
                        <table className="min-w-full divide-y divide-slate-200">
                            <thead className="bg-slate-50">
                            {table.getHeaderGroups().map(headerGroup => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map(header => (
                                        <th
                                            key={header.id}
                                            scope="col"
                                            className="px-6 py-3 text-left text-xs font-medium text-slate-500 tracking-wider"
                                        >
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                            </thead>
                            <tbody className="bg-white divide-y divide-slate-200">
                            {!coverageAreas?.length ? (
                                <tr>
                                    <td
                                        colSpan={4}
                                        className="px-6 py-4 text-sm text-center text-slate-500"
                                    >
                                        No coverage areas added yet
                                    </td>
                                </tr>
                            ) : (
                                table.getRowModel().rows.map(row => (
                                    <tr key={row.id}>
                                        {row.getVisibleCells().map(cell => (
                                            <td
                                                key={cell.id}
                                                className="px-6 py-4 whitespace-nowrap text-sm text-slate-900"
                                            >
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext()
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};